import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCertifications } from "../Query/useCertifications";
import useQuestionListDetails, {
  useCreateQuestionList,
  useUpdateQuestionListPartial,
} from "../Query/useQuestions";
import { Category, Question } from "../Models/questionListTypes";
import {
  QuestionListPartialCategory,
  QuestionListPartialQuestion,
} from "../Models/question";

export const useQuestionListForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { item, action } = location.state || {};
  const { t } = useTranslation("createQuestionList");

  const {
    data: certifications,
    isLoading: isCertificationsLoading,
    isError: isCertificationsError,
    error: certificationsError,
  } = useCertifications();

  const [selectedCertification, setSelectedCertification] = useState<
    number | null
  >(null);
  const [listName, setListName] = useState(
    t("createQuestionList.defaultListName"),
  );
  const [categories, setCategories] = useState<Category[]>([]);
  const [newQuestionTexts, setNewQuestionTexts] = useState<
    Record<string, string>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: detailsData,
    isLoading: isDetailsLoading,
    isError: isDetailsError,
  } = useQuestionListDetails(item?.id);

  const { mutate: createQuestionList } = useCreateQuestionList();
  const { mutate: updateQuestionListPartial } = useUpdateQuestionListPartial();
  const originalCategoriesRef = useRef<Category[]>([]);

  const [dirtyQuestionIds, setDirtyQuestionIds] = useState<Set<string>>(
    new Set(),
  );
  const [dirtyCategoryIds, setDirtyCategoryIds] = useState<Set<string>>(
    new Set(),
  );
  const [removedCategoryKeys, setRemovedCategoryKeys] = useState<string[]>([]);

  useEffect(() => {
    if (action === "edit" && item?.id) {
      if (isDetailsError) {
        navigate("/questions_list");
        return;
      }

      if (detailsData) {
        setListName(detailsData.name);
        setSelectedCertification(detailsData.certification_category_id);

        const transformedCategories: Category[] = detailsData.categories
          .sort((a, b) => {
            const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          })
          .map((cat) => {
            const transformedQuestions = [...cat.questions]
              .sort((a, b) => {
                const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
              })
              .map((q) => ({
                id: String(q.id),
                text: q.question,
                altText: q.alt_text,
                questionKey: q.question_key,
                order: q.order,
              }));

            return {
              id: String(cat.id),
              name: cat.name,
              subtitle: cat.subtitle,
              description: cat.description,
              extraTable: cat.extra_table,
              extraTableProperties: cat.extra_table_properties?.names || [""],
              questionCategoryKey: cat.question_category_key,
              questions: transformedQuestions,
              order: cat.order,
            };
          });
        setCategories(transformedCategories);
        originalCategoriesRef.current = JSON.parse(
          JSON.stringify(transformedCategories),
        );
        setDirtyQuestionIds(new Set());
        setDirtyCategoryIds(new Set());
        setRemovedCategoryKeys([]);
      }
    } else if (action === "create") {
      setListName(t("createQuestionList.newQuestionList"));
      setSelectedCertification(null);
      setCategories([]);
      setRemovedCategoryKeys([]);
      setDirtyQuestionIds(new Set());
      setDirtyCategoryIds(new Set());
    }
  }, [item, action, navigate, t, detailsData, isDetailsError]);

  const markCategoryDirty = useCallback((categoryId: string) => {
    setDirtyCategoryIds((prev) => {
      const next = new Set(prev);
      next.add(categoryId);
      return next;
    });
  }, []);

  const markQuestionDirty = useCallback(
    (categoryId: string, questionId: string) => {
      setDirtyQuestionIds((prev) => {
        const next = new Set(prev);
        next.add(questionId);
        return next;
      });

      setDirtyCategoryIds((prev) => {
        const next = new Set(prev);
        next.add(categoryId);
        return next;
      });
    },
    [],
  );

  const updateCategory = useCallback(
    (categoryId: string, updates: Partial<Category>) => {
      setCategories((prevCategories) =>
        prevCategories.map((cat) =>
          cat.id === categoryId
            ? {
                ...cat,
                ...updates,
              }
            : cat,
        ),
      );

      markCategoryDirty(categoryId);
    },
    [markCategoryDirty],
  );

  const addCategory = useCallback(() => {
    const newCategory: Category = {
      id: `category-${Date.now()}`,
      name: t("createQuestionList.categoryNameTemplate", {
        number: categories.length + 1,
      }),
      questions: [],
      order: categories.length + 1,
    };

    setCategories((prevCategories) => [...prevCategories, newCategory]);
    markCategoryDirty(newCategory.id);
  }, [categories.length, t, markCategoryDirty]);

  const removeCategory = useCallback(
    (categoryId: string) => {
      const removedCat = categories.find((cat) => cat.id === categoryId);

      if (removedCat?.questionCategoryKey) {
        setRemovedCategoryKeys((prev) => [
          ...prev,
          removedCat.questionCategoryKey!,
        ]);
      }
      setCategories((prevCategories) => {
        const remainingCategories = prevCategories.filter(
          (cat) => cat.id !== categoryId,
        );

        return remainingCategories.map((category, index) => ({
          ...category,
          order: index + 1,
        }));
      });

      setNewQuestionTexts((prev) => {
        const next = { ...prev };
        delete next[categoryId];
        return next;
      });

      setDirtyCategoryIds((prev) => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
    },
    [categories],
  );

  const addQuestion = useCallback(
    (categoryId: string, questionText: string) => {
      if (!questionText.trim()) {
        return;
      }

      const newQuestion: Question = {
        id: `question-${Date.now()}`,
        text: questionText.trim(),
        order: 0,
      };

      setCategories((prevCategories) =>
        prevCategories.map((cat) => {
          if (cat.id !== categoryId) {
            return cat;
          }

          const newOrder = cat.questions.length + 1;

          const questionWithOrder = {
            ...newQuestion,
            order: newOrder,
          };

          return {
            ...cat,
            questions: [...cat.questions, questionWithOrder],
          };
        }),
      );

      setNewQuestionTexts((prev) => ({
        ...prev,
        [categoryId]: "",
      }));

      markQuestionDirty(categoryId, newQuestion.id);
    },
    [markQuestionDirty],
  );

  const updateQuestion = useCallback(
    (categoryId: string, questionId: string, newText: string) => {
      setCategories((prevCategories) =>
        prevCategories.map((cat) =>
          cat.id === categoryId
            ? {
                ...cat,
                questions: cat.questions.map((q) =>
                  q.id === questionId
                    ? {
                        ...q,
                        text: newText,
                      }
                    : q,
                ),
              }
            : cat,
        ),
      );

      markQuestionDirty(categoryId, questionId);
    },
    [markQuestionDirty],
  );

  const removeQuestion = useCallback(
    (categoryId: string, questionId: string) => {
      setCategories((prevCategories) =>
        prevCategories.map((cat) => {
          if (cat.id !== categoryId) {
            return cat;
          }

          const remainingQuestions = cat.questions.filter(
            (q) => q.id !== questionId,
          );

          const reorderedQuestions = remainingQuestions.map(
            (question, index) => ({
              ...question,
              order: index + 1,
            }),
          );

          return {
            ...cat,
            questions: reorderedQuestions,
          };
        }),
      );

      markCategoryDirty(categoryId);

      setDirtyQuestionIds((prev) => {
        const next = new Set(prev);
        next.delete(questionId);
        return next;
      });
    },
    [markCategoryDirty],
  );

  const reorderQuestions = useCallback(
    (categoryId: string, reorderedQuestions: Category["questions"]) => {
      const questionsWithOrder = reorderedQuestions.map((question, index) => ({
        ...question,
        order: index + 1,
      }));

      setCategories((prevCategories) =>
        prevCategories.map((cat) =>
          cat.id === categoryId
            ? {
                ...cat,
                questions: questionsWithOrder,
              }
            : cat,
        ),
      );

      markCategoryDirty(categoryId);

      setDirtyQuestionIds((prev) => {
        const next = new Set(prev);

        questionsWithOrder.forEach((question) => {
          next.add(question.id);
        });

        return next;
      });
    },
    [markCategoryDirty],
  );

  const reorderCategories = useCallback((reorderedCategories: Category[]) => {
    const categoriesWithOrder = reorderedCategories.map((category, index) => ({
      ...category,
      order: index + 1,
    }));

    setCategories(categoriesWithOrder);

    setDirtyCategoryIds((prev) => {
      const next = new Set(prev);

      categoriesWithOrder.forEach((category) => {
        next.add(category.id);
      });

      return next;
    });
  }, []);

  const updateNewQuestionText = useCallback(
    (categoryId: string, text: string) => {
      setNewQuestionTexts((prev) => ({
        ...prev,
        [categoryId]: text,
      }));
    },
    [],
  );

  const updateQuestionAltText = useCallback(
    (categoryId: string, questionId: string, altText: string) => {
      setCategories((prevCategories) =>
        prevCategories.map((cat) =>
          cat.id === categoryId
            ? {
                ...cat,
                questions: cat.questions.map((q) =>
                  q.id === questionId
                    ? {
                        ...q,
                        altText,
                      }
                    : q,
                ),
              }
            : cat,
        ),
      );

      markQuestionDirty(categoryId, questionId);
    },
    [markQuestionDirty],
  );

  const addExtraFlag = useCallback(
    (
      categoryId: string,
      extraTable: boolean,
      extraTableProperties: string[] = [""],
    ) => {
      setCategories((prevCategories) =>
        prevCategories.map((cat) =>
          cat.id === categoryId
            ? {
                ...cat,
                extraTable,
                extraTableProperties: extraTable
                  ? extraTableProperties
                  : undefined,
              }
            : cat,
        ),
      );

      markCategoryDirty(categoryId);
    },
    [markCategoryDirty],
  );

  const hasDirtyChanges = useMemo(
    () =>
      dirtyCategoryIds.size > 0 ||
      removedCategoryKeys.length > 0 ||
      listName !== (action === "edit" ? detailsData?.name : ""),
    [dirtyCategoryIds, removedCategoryKeys, listName, detailsData, action],
  );

  const handleSubmit = async () => {
    if (!selectedCertification) {
      return;
    }

    if (
      action === "edit" &&
      categories.length === 0 &&
      removedCategoryKeys.length === 0
    ) {
      return;
    }

    if (action !== "edit" && categories.length === 0) {
      return;
    }

    setIsSubmitting(true);

    if (action === "edit" && item?.id) {
      const originalCategories = originalCategoriesRef.current;

      const originalCatMap = new Map(
        originalCategories
          .filter((cat) => cat.questionCategoryKey)
          .map((cat) => [cat.questionCategoryKey!, cat]),
      );

      const currentCatKeys = new Set(
        categories
          .filter((cat) => cat.questionCategoryKey)
          .map((cat) => cat.questionCategoryKey!),
      );

      const categoriesPayload: QuestionListPartialCategory[] = [];

      for (const origCat of originalCategories) {
        if (
          origCat.questionCategoryKey &&
          !currentCatKeys.has(origCat.questionCategoryKey)
        ) {
          categoriesPayload.push({
            _action: "delete",
            category_key: origCat.questionCategoryKey,
          });
        }
      }

      for (const cat of categories) {
        if (!cat.questionCategoryKey) {
          categoriesPayload.push({
            _action: "create",
            category_name: cat.name,
            category_subtitle: cat.subtitle,
            category_description: cat.description,
            order: cat.order,
            extra_table: cat.extraTable,
            extra_table_properties: cat.extraTable
              ? {
                  names: cat.extraTableProperties || [""],
                }
              : null,
            questions: cat.questions.map(
              (q) =>
                ({
                  _action: "create",
                  question: q.text,
                  alt_text: q.altText,
                  order: q.order,
                }) as QuestionListPartialQuestion & {
                  order?: number;
                },
            ),
          });

          continue;
        }

        if (dirtyCategoryIds.has(cat.id)) {
          const origCat = originalCatMap.get(cat.questionCategoryKey);

          const currentQuestionKeys = new Set(
            cat.questions
              .filter((q) => q.questionKey)
              .map((q) => q.questionKey!),
          );

          const questionsPayload: Array<
            QuestionListPartialQuestion & {
              order?: number;
            }
          > = [];

          for (const origQ of origCat?.questions || []) {
            if (
              origQ.questionKey &&
              !currentQuestionKeys.has(origQ.questionKey)
            ) {
              questionsPayload.push({
                _action: "delete",
                question_key: origQ.questionKey,
              });
            }
          }

          for (const q of cat.questions) {
            if (!q.questionKey) {
              questionsPayload.push({
                _action: "create",
                question: q.text,
                alt_text: q.altText,
                order: q.order,
              });
            } else {
              questionsPayload.push({
                question_key: q.questionKey,
                question: q.text,
                alt_text: q.altText,
                order: q.order,
              });
            }
          }

          categoriesPayload.push({
            category_key: cat.questionCategoryKey,
            category_name: cat.name,
            category_subtitle: cat.subtitle,
            category_description: cat.description,
            order: cat.order,
            extra_table: cat.extraTable,
            extra_table_properties: cat.extraTable
              ? {
                  names: cat.extraTableProperties || [""],
                }
              : null,
            questions: questionsPayload,
          });
        }
      }

      const listMetaUnchanged =
        item.list_name === listName &&
        item.certification_category_id === selectedCertification;

      if (categoriesPayload.length === 0 && listMetaUnchanged) {
        setIsSubmitting(false);
        return;
      }

      const data = {
        list_name: listName,
        certification_category_id: selectedCertification,
        categories: categoriesPayload,
      };

      updateQuestionListPartial(
        {
          id: item.id,
          data,
        },
        {
          onSuccess: () => {
            originalCategoriesRef.current = JSON.parse(
              JSON.stringify(categories),
            );

            setDirtyQuestionIds(new Set());
            setDirtyCategoryIds(new Set());
            setRemovedCategoryKeys([]);

            navigate("/questions_list");
          },
          onSettled: () => {
            setIsSubmitting(false);
          },
        },
      );
    } else {
      const data = {
        list_name: listName,
        certification_category_id: selectedCertification,
        categories: categories.map((cat, index) => ({
          category_name: cat.name,
          category_subtitle: cat.subtitle,
          category_description: cat.description,
          order: index + 1,
          extra_table: cat.extraTable,
          extra_table_properties: cat.extraTable
            ? {
                names: cat.extraTableProperties || [""],
              }
            : undefined,
          questions: cat.questions.map((q) => ({
            question: q.text,
            alt_text: q.altText,
            order: q.order,
          })),
        })),
      };

      createQuestionList(data, {
        onSuccess: () => navigate("/questions_list"),
        onSettled: () => setIsSubmitting(false),
      });
    }
  };

  return {
    certifications,
    isCertificationsLoading,
    isCertificationsError,
    certificationsError,
    selectedCertification,
    setSelectedCertification,
    listName,
    setListName,
    categories,
    newQuestionTexts,
    isSubmitting,
    isLoadingDetails: isDetailsLoading,
    action,
    hasDirtyChanges,
    dirtyCategoryIds,
    dirtyQuestionIds,
    addCategory,
    removeCategory,
    updateCategory,
    addQuestion,
    removeQuestion,
    updateNewQuestionText,
    handleSubmit,
    updateQuestion,
    updateQuestionAltText,
    addExtraFlag,
    reorderQuestions,
    reorderCategories,
  };
};
